import { useState, useEffect, useCallback } from 'react'
import type { PieceSymbol } from '../chessEngine'
import type { ChessDifficulty, RecognizeQuestion } from '../chessData'
import { generateRecognizeQuestions } from '../chessData'
import { useTranslation } from '../../../i18n/LanguageContext'
import { useGameCompletion } from '../../../hooks/useGameCompletion'
import GamePlayHeader from '../../GamePlayHeader'
import SaveErrorBanner from '../../SaveErrorBanner'
import { COLORS } from '../../../styles/tokens'

interface Props {
  difficulty:  ChessDifficulty
  onCompleted: (stats: { moves: number; startTime: number | null; endTime: number | null; pairsTotal: number; pairsMatched: number }) => void
  onBack:      () => void
}

interface State {
  questions:      RecognizeQuestion[]
  currentIdx:     number
  answered:       number
  moves:          number
  correctFlashId: PieceSymbol | null
  wrongId:        PieceSymbol | null
  startTime:      number | null
  endTime:        number | null
  gameComplete:   boolean
}

const QUESTIONS_PER_GAME = 10

function init(difficulty: ChessDifficulty): State {
  return {
    questions:      generateRecognizeQuestions(difficulty, QUESTIONS_PER_GAME),
    currentIdx:     0,
    answered:       0,
    moves:          0,
    correctFlashId: null,
    wrongId:        null,
    startTime:      null,
    endTime:        null,
    gameComplete:   false,
  }
}

export default function RecognizeMode({ difficulty, onCompleted, onBack }: Props) {
  const { t } = useTranslation()
  const ch    = t.chess
  const [state, setState] = useState<State>(() => init(difficulty))

  // Advance after correct-answer flash
  useEffect(() => {
    if (state.correctFlashId === null) return
    const timer = setTimeout(() => {
      setState((prev) => {
        const nextIdx = prev.currentIdx + 1
        if (nextIdx >= prev.questions.length) {
          return { ...prev, correctFlashId: null, gameComplete: true, endTime: Date.now() }
        }
        return { ...prev, currentIdx: nextIdx, correctFlashId: null }
      })
    }, 700)
    return () => clearTimeout(timer)
  }, [state.correctFlashId])

  // Clear wrong flash
  useEffect(() => {
    if (!state.wrongId) return
    const timer = setTimeout(() => setState((prev) => ({ ...prev, wrongId: null })), 650)
    return () => clearTimeout(timer)
  }, [state.wrongId])

  const selectOption = useCallback((piece: PieceSymbol) => {
    setState((prev) => {
      if (prev.correctFlashId || prev.gameComplete) return prev
      const current   = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (piece === current.pieceType) {
        return { ...prev, moves, startTime, answered: prev.answered + 1, correctFlashId: piece }
      }
      return { ...prev, moves, startTime, wrongId: piece }
    })
  }, [])

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  state.gameComplete,
    activitySlug:  'chess',
    difficulty,
    mode:          'recognize',
    moves:         state.moves,
    startTime:     state.startTime,
    endTime:       state.endTime,
    pairsTotal:    QUESTIONS_PER_GAME,
    pairsMatched:  state.answered,
    onCompleted:   () => onCompleted({
      moves:        state.moves,
      startTime:    state.startTime,
      endTime:      state.endTime,
      pairsTotal:   QUESTIONS_PER_GAME,
      pairsMatched: state.answered,
    }),
  })

  const question = state.questions[state.currentIdx]
  const progress = (state.currentIdx / QUESTIONS_PER_GAME) * 100

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      <GamePlayHeader
        title={ch.title}
        subtitle={ch.modes.recognize.label}
        stats={[
          { value: state.answered, label: ch.recognize.correct },
          { value: `${state.currentIdx + 1}/${QUESTIONS_PER_GAME}`, label: ch.moves },
        ]}
        progress={progress}
        progressNow={state.currentIdx}
        progressMax={QUESTIONS_PER_GAME}
        onBack={onBack}
        backLabel={ch.backToDashboard}
      />

      <div style={s.content}>
        {/* Piece display */}
        <div style={s.pieceBox}>
          <span style={s.pieceSymbol}>{question.symbol}</span>
        </div>

        <p style={s.instruction}>{ch.recognize.instruction}</p>

        {/* Option buttons */}
        <div style={s.optionsGrid}>
          {question.options.map((piece) => {
            const isCorrectFlash = state.correctFlashId === piece
            const isWrong        = state.wrongId === piece
            return (
              <button
                key={piece}
                onClick={() => selectOption(piece)}
                disabled={!!state.correctFlashId}
                className={isWrong ? 'wm-shake' : ''}
                style={{
                  ...s.optionBtn,
                  backgroundColor: isCorrectFlash ? '#2D6A4F' : isWrong ? '#c0392b' : COLORS.greenDark,
                  transform:       isCorrectFlash ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                <span style={s.pieceName}>{ch.recognize.pieces[piece]}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root:        { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: COLORS.bgPage },
  content:     { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', padding: '1rem' },
  pieceBox:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', background: '#fff', borderRadius: 16, padding: '1.5rem 2.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
  pieceSymbol: { fontSize: '5rem', lineHeight: 1 },
  instruction: { fontSize: '1.125rem', color: '#333', fontStyle: 'italic', margin: 0, textAlign: 'center' },
  optionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', width: '100%', maxWidth: 360 },
  optionBtn: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            '0.25rem',
    padding:        '0.875rem 0.5rem',
    borderRadius:   12,
    border:         'none',
    cursor:         'pointer',
    transition:     'all 0.15s ease',
    minHeight:      80,
    color:          '#fff',
  },
  pieceName:    { fontSize: '1.375rem', fontWeight: 700 },
}
